import { useEffect, useMemo, useRef, useState } from 'react'
import { RobotActions } from 'store/actions/robot'
import store from 'store/store'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import { MAX_LIVE_EXECUTION_TRAIL_POINTS } from 'utils/constants'
import { capTrailPoints } from 'utils/robotTrajectory'
import { telemetryPatchFromWs } from 'utils/wsTelemetryPatch'

/** Payload of `gausium_live_execution` WebSocket messages (GS map + execution snapshot). */
export type GausiumLiveExecutionWsPayload = {
  state?: string | null
  task_name?: string | null
  map?: {
    id?: number | null
    external_id?: string | null
    name?: string | null
    preview_url?: string | null
    position?: { x: number; y: number; angle: number } | null
  } | null
  source_ts?: number | null
  age_ms?: number | null
  recorded_at?: number | null
  poll_elapsed_ms?: number | null
  trail_points?: Array<{ x: number; y: number }>
  trail_session_key?: string | null
  telemetry?: unknown
}

/**
 * Subscribes to backend GS fast-poll stream (one shared poller per robot on WS worker).
 * When `enabled` is false, sends unsubscribe so GS REST is not polled for this viewer.
 */
export function useGausiumLiveExecutionWebSocket(
  robotId: number | null | undefined,
  enabled: boolean,
  options?: { patchTelemetry?: boolean }
) {
  const patchTelemetry = options?.patchTelemetry !== false
  const [liveExecutionWs, setLiveExecutionWs] = useState<GausiumLiveExecutionWsPayload | null>(null)
  const lastWsRecordedAtRef = useRef<number | null>(null)
  const lastWsMessageAtRef = useRef<number | null>(null)
  const [liveExecutionWsIntervalMs, setLiveExecutionWsIntervalMs] = useState<number | null>(null)

  useEffect(() => {
    if (!enabled || robotId == null || !Number.isFinite(Number(robotId))) {
      setLiveExecutionWs(null)
      setLiveExecutionWsIntervalMs(null)
      lastWsRecordedAtRef.current = null
      lastWsMessageAtRef.current = null
      return
    }

    const parsedRobotId = Number(robotId)
    const callback = (data: GausiumLiveExecutionWsPayload) => {
      const nowMs = Date.now()
      const prevMessageAtMs = lastWsMessageAtRef.current
      if (prevMessageAtMs != null) {
        const wsDeltaMs = Math.round(nowMs - prevMessageAtMs)
        if (wsDeltaMs > 0 && wsDeltaMs < 120000) {
          setLiveExecutionWsIntervalMs(wsDeltaMs)
        }
      }
      lastWsMessageAtRef.current = nowMs

      const currentRecordedAt = Number(data?.recorded_at)
      if (Number.isFinite(currentRecordedAt) && currentRecordedAt > 0) {
        const prev = lastWsRecordedAtRef.current
        if (prev != null) {
          const recordedDeltaMs = Math.round((currentRecordedAt - prev) * 1000)
          if (prevMessageAtMs == null && recordedDeltaMs > 0 && recordedDeltaMs < 120000) {
            setLiveExecutionWsIntervalMs(recordedDeltaMs)
          }
        }
        lastWsRecordedAtRef.current = currentRecordedAt
      }

      if (patchTelemetry) {
        const telemetryPatch = telemetryPatchFromWs(data?.telemetry as any)
        if (telemetryPatch) {
          store.dispatch(RobotActions.setTelemetry(telemetryPatch))
        }
      }
      setLiveExecutionWs(
        data
          ? {
              ...data,
              trail_points: capTrailPoints(data.trail_points, MAX_LIVE_EXECUTION_TRAIL_POINTS),
            }
          : null
      )
    }

    WebsocketManager.sendMessage(
      JSON.stringify({
        type: 'gausium_live_execution.subscribe',
        robot_id: parsedRobotId,
      })
    )
    WebsocketManager.addEventListener(SOCKET_MESSAGES.GAUSIUM_LIVE_EXECUTION, callback)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.GAUSIUM_LIVE_EXECUTION, callback)
      WebsocketManager.sendMessage(JSON.stringify({ type: 'gausium_live_execution.unsubscribe' }))
      setLiveExecutionWs(null)
      setLiveExecutionWsIntervalMs(null)
      lastWsRecordedAtRef.current = null
      lastWsMessageAtRef.current = null
    }
  }, [robotId, enabled, patchTelemetry])

  const liveExecutionWsIsFresh = useMemo(() => {
    const ageFromPayload = Number(liveExecutionWs?.age_ms)
    if (Number.isFinite(ageFromPayload) && ageFromPayload >= 0) {
      return ageFromPayload < 7000
    }
    if (!liveExecutionWs?.recorded_at) return false
    const recordedAtMs = Number(liveExecutionWs.recorded_at) * 1000
    if (!Number.isFinite(recordedAtMs)) return false
    return Date.now() - recordedAtMs < 5000
  }, [liveExecutionWs])

  return { liveExecutionWs, liveExecutionWsIsFresh, liveExecutionWsIntervalMs }
}
